import { useState, useMemo } from "react";
import { DEMO_PROFILE } from "@/data/demoUser";
import {
  buildSignosFromForm,
  buildCondicionesArray,
  buildF2Firestore,
  buildF3Firestore,
  buildF4Firestore,
  buildF5Firestore,
  translateFormKeys,
} from "@/data/loinc";
import { getAutoDetectedConditions, calculateIMC } from "@/pages/FormF1/helpers";
import {
  Send, Lock, Copy, Check, ChevronDown, Clock, AlertCircle,
  CheckCircle2, Zap, Database, Activity, FileText,
} from "lucide-react";

const DEV_PIN = "we360dev";
const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_URL = RAW_BACKEND_URL === "http://localhost:8001" && window.location.hostname !== "localhost"
  ? ""
  : RAW_BACKEND_URL;
const GCP_DIRECT_URL = "https://we360-dev-ep.saluddigital360.com"; // solo referencia, no usar directo
const TEST_EMAIL = "demo_user_jose_perez@demo.we360.com";

function generatePayloads() {
  const p = DEMO_PROFILE;
  const signos = buildSignosFromForm({
    peso: p.peso, talla: p.talla, cintura: p.cintura,
    paSistolica: p.paSistolica, paDiastolica: p.paDiastolica,
    glucemia: p.glucemia, oximetria: p.oximetria,
    tabaco: p.tabaco, alcohol: p.alcohol, sueno: p.sueno,
  });
  const imc = calculateIMC(p.peso, p.talla);
  const autoIds = getAutoDetectedConditions(
    { peso: p.peso, talla: p.talla, paSistolica: p.paSistolica, paDiastolica: p.paDiastolica, glucemia: p.glucemia }, imc
  );
  const condiciones = buildCondicionesArray(p.condicionIds || [], autoIds, p.condicionesAdicionales || []);

  const f1 = {
    form: "F1", userId: TEST_EMAIL,
    data: translateFormKeys("F1", {
      email: p.email, whatsapp: p.whatsapp, nombres: p.nombres, apellidos: p.apellidos,
      fechaNacimiento: p.fechaNacimiento, sexo: p.sexo, estadoCivil: p.estadoCivil,
      nivelEducativo: p.nivelEducativo, profesion: p.profesion, ocupacion: p.ocupacion,
      residencia: p.residencia, signos, condiciones,
      usaMedicamentos: p.usaMedicamentos, medicamentos: p.medicamentos,
      consentimiento: p.consentimiento,
      createdAt: new Date().toISOString(), completedAt: new Date().toISOString(),
    }),
  };
  const f2 = { form: "F2", userId: TEST_EMAIL, data: translateFormKeys("F2", buildF2Firestore(p.f2Results)) };
  const f3 = { form: "F3", userId: TEST_EMAIL, data: translateFormKeys("F3", buildF3Firestore(p.f3Results)) };
  const f4 = { form: "F4", userId: TEST_EMAIL, data: translateFormKeys("F4", buildF4Firestore(p.f4Results)) };
  const f5 = { form: "F5", userId: TEST_EMAIL, data: translateFormKeys("F5", buildF5Firestore(p.f5Results)) };
  return { F1: f1, F2: f2, F3: f3, F4: f4, F5: f5 };
}

// ─── Presets ────────────────────────────────────────────────
function getPresets(payloads) {
  return [
    { id: "post-f1", label: "POST F1", sub: "Perfil de Salud", method: "POST", url: `${BACKEND_URL}/api/gcp/forms`, body: payloads.F1, color: "#e94560" },
    { id: "post-f2", label: "POST F2", sub: "Evaluaci\u00f3n F\u00edsica", method: "POST", url: `${BACKEND_URL}/api/gcp/forms`, body: payloads.F2, color: "#e94560" },
    { id: "post-f3", label: "POST F3", sub: "IPAQ", method: "POST", url: `${BACKEND_URL}/api/gcp/forms`, body: payloads.F3, color: "#e94560" },
    { id: "post-f4", label: "POST F4", sub: "Nutrici\u00f3n", method: "POST", url: `${BACKEND_URL}/api/gcp/forms`, body: payloads.F4, color: "#e94560" },
    { id: "post-f5", label: "POST F5", sub: "Emocional", method: "POST", url: `${BACKEND_URL}/api/gcp/forms`, body: payloads.F5, color: "#e94560" },
    { id: "get-history", label: "GET History", sub: "Historial", method: "GET", url: `${BACKEND_URL}/api/gcp/query?endpoint=/api/patients/history&email=${TEST_EMAIL}`, body: null, color: "#4ade80" },
    { id: "get-everything", label: "GET Everything", sub: "Datos completos", method: "GET", url: `${BACKEND_URL}/api/gcp/query?endpoint=/api/patients/everything&email=${TEST_EMAIL}`, body: null, color: "#4ade80" },
    { id: "get-metrics", label: "GET M\u00e9trica", sub: "Peso (LOINC 29463-7)", method: "GET", url: `${BACKEND_URL}/api/gcp/query?endpoint=/api/patients/metrics?code=29463-7&email=${TEST_EMAIL}`, body: null, color: "#60a5fa" },
  ];
}

// ─── Components ─────────────────────────────────────────────
function PinGate({ onUnlock }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);
  const handleSubmit = (e) => {
    e.preventDefault();
    if (pin === DEV_PIN) onUnlock();
    else { setError(true); setTimeout(() => setError(false), 2000); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#1a1a2e" }}>
      <form onSubmit={handleSubmit} className="w-full max-w-xs p-6 rounded-2xl" style={{ background: "#16213e" }}>
        <div className="flex justify-center mb-4">
          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "#0f3460" }}>
            <Lock size={20} style={{ color: "#e94560" }} />
          </div>
        </div>
        <h2 className="text-center text-sm font-bold mb-1" style={{ color: "#eee" }}>API Tester</h2>
        <p className="text-center text-xs mb-4" style={{ color: "#8899aa" }}>Ingresa el PIN de desarrollo</p>
        <input
          type="password" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="PIN" autoFocus
          className="w-full px-4 py-2.5 rounded-lg text-center text-sm font-mono tracking-widest mb-3 outline-none"
          style={{ background: "#0f3460", border: error ? "2px solid #e94560" : "2px solid transparent", color: "#eee" }}
          data-testid="pin-input"
        />
        {error && <p className="text-xs text-center mb-2" style={{ color: "#e94560" }}>PIN incorrecto</p>}
        <button type="submit" className="w-full py-2.5 rounded-lg text-sm font-semibold cursor-pointer"
          style={{ background: "#e94560", color: "white", border: "none" }} data-testid="pin-submit">
          Entrar
        </button>
      </form>
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <button onClick={handleCopy} type="button"
      className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium cursor-pointer"
      style={{ background: copied ? "#1a4d2e" : "#0f3460", color: copied ? "#4ade80" : "#8899aa", border: "none" }}>
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? "Copiado" : "Copiar"}
    </button>
  );
}

function ResponsePanel({ response }) {
  if (!response) return null;
  const isSuccess = response.status >= 200 && response.status < 300;
  const isError = response.status >= 400 || response.status === 0;
  return (
    <div className="rounded-xl overflow-hidden" style={{ background: "#0f3460", border: "1px solid #1a4d6e" }}>
      <div className="flex items-center justify-between px-4 py-2" style={{ borderBottom: "1px solid #1a4d6e" }}>
        <div className="flex items-center gap-2">
          {isSuccess && <CheckCircle2 size={14} style={{ color: "#4ade80" }} />}
          {isError && <AlertCircle size={14} style={{ color: "#e94560" }} />}
          {!isSuccess && !isError && <AlertCircle size={14} style={{ color: "#f59e0b" }} />}
          <span className="text-xs font-bold" style={{ color: isSuccess ? "#4ade80" : isError ? "#e94560" : "#f59e0b" }}>
            {response.status} {response.statusText}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Clock size={12} style={{ color: "#8899aa" }} />
          <span className="text-xs font-mono" style={{ color: "#8899aa" }}>{response.time}ms</span>
        </div>
      </div>
      <div className="relative">
        <div className="absolute top-2 right-2"><CopyButton text={response.body} /></div>
        <pre className="p-4 text-xs overflow-auto" style={{ color: "#c8d6e5", maxHeight: 400, margin: 0 }}>
          {response.body}
        </pre>
      </div>
    </div>
  );
}

// ─── Main Tester ────────────────────────────────────────────
function ApiTester() {
  const payloads = useMemo(() => generatePayloads(), []);
  const presets = useMemo(() => getPresets(payloads), [payloads]);

  const [method, setMethod] = useState("POST");
  const [url, setUrl] = useState(`${BACKEND_URL}/api/forms`);
  const [json, setJson] = useState(JSON.stringify(payloads.F1, null, 2));
  const [activePreset, setActivePreset] = useState("post-f1");
  const [sending, setSending] = useState(false);
  const [response, setResponse] = useState(null);
  const [history, setHistory] = useState([]);

  const handlePreset = (preset) => {
    setActivePreset(preset.id);
    setMethod(preset.method);
    setUrl(preset.url);
    setJson(preset.body ? JSON.stringify(preset.body, null, 2) : "");
    setResponse(null);
  };

  const handleSend = async () => {
    if (!url.trim()) return;
    setSending(true);
    setResponse(null);
    const start = performance.now();
    try {
      const isGet = method === "GET";
      let parsedJson;
      if (!isGet && json.trim()) {
        try { parsedJson = JSON.parse(json); } catch {
          setResponse({ status: 0, statusText: "JSON inv\u00e1lido", body: "Error: El JSON del payload no es v\u00e1lido", time: 0 });
          setSending(false);
          return;
        }
      }
      const opts = { method, headers: { "Content-Type": "application/json" } };
      if (!isGet && parsedJson) opts.body = JSON.stringify(parsedJson);
      const res = await fetch(url.trim(), opts);
      const elapsed = Math.round(performance.now() - start);
      let body;
      const ct = res.headers.get("content-type") || "";
      if (ct.includes("json")) body = JSON.stringify(await res.json(), null, 2);
      else body = await res.text();
      const result = { status: res.status, statusText: res.statusText, body, time: elapsed };
      setResponse(result);
      setHistory((prev) => [{ preset: activePreset, method, url, ...result, ts: new Date().toLocaleTimeString() }, ...prev].slice(0, 20));
    } catch (err) {
      const elapsed = Math.round(performance.now() - start);
      const result = { status: 0, statusText: "Error de red", body: err.message, time: elapsed };
      setResponse(result);
      setHistory((prev) => [{ preset: activePreset, method, url, ...result, ts: new Date().toLocaleTimeString() }, ...prev].slice(0, 20));
    }
    setSending(false);
  };

  const isGet = method === "GET";
  const lineCount = json ? json.split("\n").length : 0;

  return (
    <div className="min-h-screen" style={{ background: "#1a1a2e" }}>
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between" style={{ background: "#16213e", borderBottom: "1px solid #0f3460" }}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded flex items-center justify-center" style={{ background: "#e94560" }}>
            <Send size={14} color="white" />
          </div>
          <span className="text-sm font-bold" style={{ color: "#eee" }}>WE360 API Tester</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-mono px-2 py-0.5 rounded" style={{ background: "#0f3460", color: "#4ade80" }}>
            Backend: GCP Cloud Run
          </span>
          <span className="text-[10px] font-mono px-2 py-0.5 rounded" style={{ background: "#0f3460", color: "#8899aa" }}>
            {TEST_EMAIL}
          </span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4">
        <div className="flex gap-4">
          {/* Left: Main tester */}
          <div className="flex-1 space-y-4">
            {/* Quick presets */}
            <div className="rounded-xl p-3" style={{ background: "#16213e", border: "1px solid #0f3460" }}>
              <div className="flex items-center gap-2 mb-3">
                <Zap size={14} style={{ color: "#f59e0b" }} />
                <span className="text-xs font-bold" style={{ color: "#eee" }}>Endpoints</span>
              </div>
              <div className="grid grid-cols-4 gap-2 mb-2">
                <span className="text-[10px] font-semibold col-span-4 px-1" style={{ color: "#8899aa" }}>
                  <FileText size={10} className="inline mr-1" />POST /api/forms
                </span>
                {presets.filter((p) => p.method === "POST").map((p) => (
                  <button key={p.id} onClick={() => handlePreset(p)}
                    className="py-2 px-2 rounded-lg text-xs font-semibold transition-all cursor-pointer"
                    style={{
                      background: activePreset === p.id ? p.color : "#0f3460",
                      color: activePreset === p.id ? "white" : "#8899aa",
                      border: "none",
                    }}
                    data-testid={`preset-${p.id}`}>
                    {p.label}
                    <span className="block text-[9px] font-normal opacity-75">{p.sub}</span>
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-[10px] font-semibold col-span-3 px-1 pt-1" style={{ color: "#8899aa" }}>
                  <Database size={10} className="inline mr-1" />GET /api/patients/*
                </span>
                {presets.filter((p) => p.method === "GET").map((p) => (
                  <button key={p.id} onClick={() => handlePreset(p)}
                    className="py-2 px-2 rounded-lg text-xs font-semibold transition-all cursor-pointer"
                    style={{
                      background: activePreset === p.id ? p.color : "#0f3460",
                      color: activePreset === p.id ? "white" : "#8899aa",
                      border: "none",
                    }}
                    data-testid={`preset-${p.id}`}>
                    {p.label}
                    <span className="block text-[9px] font-normal opacity-75">{p.sub}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* URL + Method */}
            <div className="flex gap-2">
              <div className="relative">
                <select value={method} onChange={(e) => setMethod(e.target.value)}
                  className="h-10 px-3 pr-7 rounded-lg text-xs font-bold appearance-none cursor-pointer"
                  style={{ background: "#0f3460", color: isGet ? "#4ade80" : "#e94560", border: "none", outline: "none" }}
                  data-testid="method-select">
                  <option value="POST">POST</option>
                  <option value="GET">GET</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                </select>
                <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: isGet ? "#4ade80" : "#e94560" }} />
              </div>
              <input type="text" value={url} onChange={(e) => setUrl(e.target.value)}
                placeholder={`${BACKEND_URL}/api/...`}
                className="flex-1 h-10 px-4 rounded-lg text-sm font-mono outline-none"
                style={{ background: "#0f3460", color: "#eee", border: "2px solid transparent" }}
                onFocus={(e) => e.target.style.borderColor = "#e94560"}
                onBlur={(e) => e.target.style.borderColor = "transparent"}
                data-testid="url-input"
              />
              <button onClick={handleSend} disabled={sending || !url.trim()}
                className="h-10 px-6 rounded-lg text-sm font-bold flex items-center gap-2 cursor-pointer transition-all"
                style={{ background: sending ? "#8899aa" : isGet ? "#059669" : "#e94560", color: "white", border: "none", opacity: !url.trim() ? 0.4 : 1 }}
                data-testid="send-btn">
                {sending ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send size={14} />}
                Enviar
              </button>
            </div>

            {/* JSON Editor (only for non-GET) */}
            {!isGet && (
              <div className="rounded-xl overflow-hidden" style={{ background: "#16213e", border: "1px solid #0f3460" }}>
                <div className="flex items-center justify-between px-4 py-2" style={{ borderBottom: "1px solid #0f3460" }}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold" style={{ color: "#8899aa" }}>Request Body</span>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: "#0f3460", color: "#8899aa" }}>
                      {lineCount} l&iacute;neas
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => { const p = presets.find((x) => x.id === activePreset); if (p?.body) setJson(JSON.stringify(p.body, null, 2)); }}
                      className="text-[10px] px-2 py-1 rounded font-medium cursor-pointer"
                      style={{ background: "#0f3460", color: "#8899aa", border: "none" }} type="button">
                      Restaurar
                    </button>
                    <CopyButton text={json} />
                  </div>
                </div>
                <textarea value={json} onChange={(e) => setJson(e.target.value)}
                  className="w-full p-4 text-xs font-mono outline-none resize-y"
                  style={{ background: "transparent", color: "#c8d6e5", minHeight: 200, maxHeight: 500, border: "none", lineHeight: 1.6 }}
                  spellCheck={false} data-testid="json-editor"
                />
              </div>
            )}

            {/* GET info */}
            {isGet && (
              <div className="rounded-xl p-4" style={{ background: "#16213e", border: "1px solid #0f3460" }}>
                <div className="flex items-center gap-2 mb-2">
                  <Database size={14} style={{ color: "#4ade80" }} />
                  <span className="text-xs font-bold" style={{ color: "#eee" }}>Consulta GET</span>
                </div>
                <p className="text-xs" style={{ color: "#8899aa" }}>
                  Sin body. Los par&aacute;metros van en la URL.
                  Puedes editar la URL directamente para cambiar el email o el c&oacute;digo LOINC.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {["29463-7:Peso", "8302-2:Estatura", "8280-0:Cintura", "8480-6:PA Sist", "1558-6:Glucemia", "2708-6:SpO2"].map((item) => {
                    const [code, label] = item.split(":");
                    return (
                      <button key={code}
                        onClick={() => setUrl(`${BACKEND_URL}/api/patients/metrics?code=${code}&email=${TEST_EMAIL}`)}
                        className="text-[10px] px-2 py-1 rounded font-mono cursor-pointer"
                        style={{ background: "#0f3460", color: "#60a5fa", border: "none" }}
                        data-testid={`metric-${code}`}>
                        {code} ({label})
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Response */}
            <ResponsePanel response={response} />
          </div>

          {/* Right: Request history */}
          <div className="w-60 shrink-0">
            <div className="rounded-xl p-3 sticky top-4" style={{ background: "#16213e", border: "1px solid #0f3460" }}>
              <div className="flex items-center gap-2 mb-3">
                <Activity size={14} style={{ color: "#8899aa" }} />
                <span className="text-xs font-bold" style={{ color: "#eee" }}>Historial</span>
                {history.length > 0 && (
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded ml-auto" style={{ background: "#0f3460", color: "#8899aa" }}>
                    {history.length}
                  </span>
                )}
              </div>
              {history.length === 0 ? (
                <p className="text-[10px] text-center py-6" style={{ color: "#556677" }}>Sin peticiones a&uacute;n</p>
              ) : (
                <div className="space-y-1.5 max-h-[calc(100vh-200px)] overflow-y-auto">
                  {history.map((h, i) => {
                    const ok = h.status >= 200 && h.status < 300;
                    return (
                      <div key={i} className="rounded-lg p-2" style={{ background: "#0f3460" }}>
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-[10px] font-bold" style={{ color: ok ? "#4ade80" : "#e94560" }}>
                            {h.method} {h.status}
                          </span>
                          <span className="text-[9px] font-mono" style={{ color: "#556677" }}>{h.time}ms</span>
                        </div>
                        <p className="text-[9px] font-mono truncate" style={{ color: "#8899aa" }}>
                          {h.url.replace(BACKEND_URL, "")}
                        </p>
                        <p className="text-[9px]" style={{ color: "#556677" }}>{h.ts}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DevApiTester() {
  const [unlocked, setUnlocked] = useState(false);
  if (!unlocked) return <PinGate onUnlock={() => setUnlocked(true)} />;
  return <ApiTester />;
}
